import { Box } from '@mui/material';
import { GridApi, IRowNode } from 'ag-grid-community';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { merge } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { createCompanyDataModel, ICompanyDataModel } from '../../../data-models/company.data-model';
import { RendererType } from '../../../data-models/field-definition';
import { ICompanySearchMeta } from '../../../data-models/field3.data-model';
import { CompanyMappingVM } from '../../../schemas/CompanyMappings.schema';
import { useCreateCompanyAndUpdateState } from '../../../services/hooks/useCreateCompanyAndUpdateState';
import { SearchType } from '../../../services/queries/MaggieCompanyQueries';
import { highlightRow } from '../../../util/ag-grid-utils';
import { companyToSearchResultWithRealId } from '../../../util/companyUtils';
import { captureUnexpectedState } from '../../../util/SentryUtils';
import { createFormField } from '../../../view-models/form.view-model';
import { CompanyFormStep } from '../../Finance2/Forms/CompanyFormStep';
import {
  mappingsGridApiAtom,
  selectedMappingsRowNodeState,
  showCreateCompanyDialogState,
} from './CompanyMappingsState';

export const initialMappingRowNodeState = atom<IRowNode<CompanyMappingVM> | null>(null);

export function useMappingsCompanyField() {
  const gridApi = useAtomValue(mappingsGridApiAtom);
  const setInitialData = useSetAtom(initialMappingRowNodeState);

  const selectedMappingsRowNode = useAtomValue(selectedMappingsRowNodeState);
  const setShowCreateCompanyDialog = useSetAtom(showCreateCompanyDialogState);
  const onAdd = useCallback(
    (name?: unknown) => {
      if (!selectedMappingsRowNode) {
        captureUnexpectedState('No mappings row data when adding company', {});
        return;
      }
      // store initial data for the form in a separate atom as selectedMappingsRowNodeState will be reset when stopEditing is called
      setInitialData(
        merge({}, selectedMappingsRowNode, {
          data: {
            company: {
              fields: {
                name: typeof name === 'string' ? name : '',
                website: selectedMappingsRowNode.data?.companySite,
              },
            },
          },
        })
      );
      // new data will be set from the form
      gridApi?.stopEditing(true);
      setShowCreateCompanyDialog(true);
    },
    [selectedMappingsRowNode, gridApi, setInitialData, setShowCreateCompanyDialog]
  );

  return useMemo(() => {
    const doOnChange = () => {
      gridApi?.stopEditing();
      gridApi?.refreshCells({ force: true });
    };
    const baseRendererMeta = {
      createOnSelection: false,
      multiSelect: false,
      addLabel: (name: unknown) => (typeof name === 'string' ? `Create Company ${name}` : 'Create Company'),
      onAdd,
    };
    const formFieldInvestran = createFormField<ICompanySearchMeta>({
      key: 'company',
      label: 'Company',
      renderer: RendererType.companySearch,
      rendererMeta: { ...baseRendererMeta, showAdd: true, searchType: SearchType.Company },
      autoFocus: true,
      fullWidth: true,
    });
    const formFieldPortfolio = createFormField<ICompanySearchMeta>({
      key: 'company',
      label: 'Company',
      renderer: RendererType.companySearch,
      rendererMeta: { ...baseRendererMeta, showAdd: false, searchType: SearchType.Portfolio },
      autoFocus: true,
      fullWidth: true,
    });
    return { formFieldInvestran, formFieldPortfolio, doOnChange };
  }, [gridApi, onAdd]);
}

export function CreateMappingCompanyDialog() {
  const { showCreateCompanyDialog, onClose, defaultValues, onCreate } = useCreateMappingsCompanyState();
  if (!showCreateCompanyDialog) {
    return null;
  }
  return (
    <BasicDialog open={showCreateCompanyDialog} onClose={onClose} title='Create Company'>
      <Box marginInline={'2rem'}>
        <CompanyFormStep defaultValues={defaultValues} onSubmit={onCreate} />
      </Box>
    </BasicDialog>
  );
}

export function useCreateMappingsCompanyState() {
  const gridApi = useAtomValue(mappingsGridApiAtom);
  const [showCreateCompanyDialog, setShowCreateCompanyDialog] = useAtom(showCreateCompanyDialogState);
  const createCompany = useCreateCompanyAndUpdateState();
  const [initialRowNode, setInitialRowNode] = useAtom(initialMappingRowNodeState);

  const onClose = useCallback(() => {
    setInitialRowNode(null);
    setShowCreateCompanyDialog(false);
  }, [setInitialRowNode, setShowCreateCompanyDialog]);

  const defaultValues = useMemo(() => {
    return createCompanyDataModel({
      name: initialRowNode?.data?.company?.fields?.name,
      website: initialRowNode?.data?.company?.fields?.website,
    });
  }, [initialRowNode]);

  const onCreate = useCallback(
    async (data: Partial<ICompanyDataModel>) => {
      if (!initialRowNode || !gridApi) {
        captureUnexpectedState('No mappings row data when adding company', {});
        return;
      }
      const res = await createCompany(data);
      if (!res) {
        return;
      }

      const updatedNodeData = {
        ...initialRowNode.data,
        company: companyToSearchResultWithRealId(res),
        modified: true,
      };
      setShowCreateCompanyDialog(false);

      updateMappingRow(initialRowNode, gridApi, updatedNodeData as Partial<ICompanyDataModel>);
    },
    [initialRowNode, gridApi, createCompany, setShowCreateCompanyDialog]
  );

  return { onCreate, onClose, defaultValues, showCreateCompanyDialog };
}

function updateMappingRow(
  initialRowNode: IRowNode | null,
  gridApi: GridApi,
  updatedNodeData?: Partial<ICompanyDataModel>
) {
  if (!initialRowNode || !gridApi || !updatedNodeData) {
    return;
  }
  const doHighlight = () => {
    highlightRow({ node: initialRowNode, api: gridApi });
    gridApi.removeEventListener('rowDataUpdated', doHighlight);
  };
  gridApi.addEventListener('rowDataUpdated', doHighlight);
  gridApi.applyTransaction({
    update: [updatedNodeData],
  });
}
